/**
 * Hook pour gérer l'état d'abonnement et calculer l'urgence du trial
 */

import { useMemo, useState } from 'react';
import type { User } from '@/types';

export type TrialUrgency = 'none' | 'info' | 'warning' | 'critical' | 'expired';

export interface SubscriptionState {
    status: string;
    isBlocked: boolean;
    isActive: boolean;
    isTrial: boolean;
    /** Carte en place et compte ouvert : plus d'essai à finir ni d'abonnement à prendre. */
    abonne: boolean;
    daysLeft: number | null;
    urgency: TrialUrgency;
    trialEndsAt: Date | null;
}

export function useSubscription(user: User | null): SubscriptionState {
    // Horloge figée au mount : suffisant pour une granularité en jours,
    // et garde le render pur (pas de Date.now() par re-render)
    const [now] = useState(() => Date.now());

    return useMemo(() => {
        const status = user?.subscriptionStatus ?? 'TRIAL';
        const trialEndsAt = user?.trialEndsAt ? new Date(user.trialEndsAt) : null;

        const isBlocked = status === 'BLOCKED' || status === 'CANCELLED';
        const isActive = status === 'ACTIVE';
        const isTrial = status === 'TRIAL';
        const abonne = !!user?.abonnementEnPlace && ['TRIAL', 'ACTIVE', 'PAST_DUE'].includes(status);

        let daysLeft: number | null = null;
        if (trialEndsAt) {
            const diff = trialEndsAt.getTime() - now;
            daysLeft = Math.ceil(diff / (1000 * 60 * 60 * 24));
        }

        let urgency: TrialUrgency = 'none';
        if (abonne) {
            urgency = 'none';
        } else if (isBlocked || (isTrial && daysLeft !== null && daysLeft <= 0)) {
            urgency = 'expired';
        } else if (isTrial && daysLeft !== null) {
            if (daysLeft <= 1) urgency = 'critical';
            else if (daysLeft <= 3) urgency = 'warning';
            else if (daysLeft <= 7) urgency = 'info';
        }

        return { status, isBlocked, isActive, isTrial, abonne, daysLeft, urgency, trialEndsAt };
    }, [user, now]);
}
